/**
 * The one function allowed to name a project's reconstruction state.
 *
 * It exists because this product got the same thing wrong twice: a job whose
 * status is `succeeded` is a *successful job*, and a successful job on this
 * deployment produces a registered camera set and a sparse point cloud. It
 * does not produce a model. A screen that reads the status and prints "3D
 * Model hazır" announces a file nobody can open.
 *
 * So the ladder below consults artifacts first and status only as a fallback:
 *
 *   validated mesh artifact   -> "3D Model hazır"
 *   sparse artifact           -> "Seyrek 3D hazır"
 *   status queued/running     -> "Sırada" / "Çalışıyor"
 *   status failed             -> "Başarısız"
 *   no job                    -> "Başlatılmadı"
 *
 * `has_final_model` and `furthest_output` both come from `domain/workflow.py`,
 * which reads the artifact table. Neither can be true because a job ended
 * well.
 */

import type { ProjectListItem } from "@/api/types";

export type ReconstructionStateLabel =
  | "Başlatılmadı"
  | "Sırada"
  | "Çalışıyor"
  | "Seyrek 3D hazır"
  | "Yoğun 3D hazır"
  | "3D Model hazır"
  | "Başarısız"
  | "İptal edildi";

export function reconstructionStateLabel(
  project: Pick<ProjectListItem, "latest_reconstruction_status" | "workflow">,
): ReconstructionStateLabel {
  const workflow = project.workflow ?? null;

  // Artifact evidence outranks job status in both directions: a mesh means a
  // model even if a later stage failed, and a succeeded job with no sparse
  // artifact is not "hazır" anything.
  if (workflow?.has_final_model) return "3D Model hazır";
  if (workflow?.furthest_output === "dense") return "Yoğun 3D hazır";
  if (workflow?.furthest_output === "sparse") return "Seyrek 3D hazır";

  switch (project.latest_reconstruction_status) {
    case "queued":
      return "Sırada";
    case "running":
      return "Çalışıyor";
    case "failed":
      return "Başarısız";
    case "cancelled":
      return "İptal edildi";
    case "succeeded":
      // Succeeded, and produced no artifact this client can name. Not a
      // failure, and emphatically not a model.
      return "Başlatılmadı";
    default:
      return "Başlatılmadı";
  }
}
